import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, Grid,
  Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, IconButton, Tooltip, Link, Autocomplete, Checkbox, FormControlLabel, Switch,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
  TaskAlt as ValidateIcon,
  Gavel as ContractIcon,
  UploadFile as UploadIcon,
  Delete as DeleteIcon,
  CloudUpload as TalanaIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import ErpEmployeeFormFields, {
  emptyEmployeeForm,
  toEmployeeForm,
  applyEmployeeFieldChange,
  toEmployeePayload,
  missingForValidation,
  REQUIRED_FOR_VALIDATION,
  employeeFormatErrors,
} from './ErpEmployeeFormFields';
import {
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
} from '@mui/icons-material';
import { EMPLOYEE_STATUS_COLORS } from './ErpEmployeesLayout';
import formatRut from '../../erp/formatRut';
import ErpDateField from '../ErpDateField';
import { contractKindFromName, isTalanaRef, refName } from '../talanaRefs';
import { formatDate } from '../formatDate';
import { MINIMUM_WAGE_CODE, minimumWageMessage } from '../quotations/quotationUtils';
import ErpTalanaDocumentsDialog, {
  TalanaDocStatusChip,
  canonicalCategory,
  knownVisibility,
} from '../export/ErpTalanaDocumentsDialog';

// [ruta, clave i18n] de cada requisito, para pintar el checklist
const REQUIRED_ROWS = REQUIRED_FOR_VALIDATION.map(([path, labelKey]) => [path, labelKey]);

const DOC_TYPES = ['id_card', 'pension_certificate', 'health_certificate', 'visa', 'other'];
// Categoría de Talana sugerida por tipo (mismas que el backend, erpTalanaDocuments.service)
const TALANA_CATEGORY_BY_TYPE = {
  id_card: 'C.I.',
  pension_certificate: 'AFP',
  health_certificate: 'SALUD',
  visa: 'PERMISO DE TRABAJO',
  other: 'Otros Documentos',
};
const TALANA_POLL_MS = 3000;

// Cotización de la persona al pasar a contrato (fase 3 del cotizador OUT/EST,
// 2026-10-13): la de la ficha, otra de su familia sin contrato, o una
// subcotización nueva (COT-0009-n) con la fecha de ingreso del contrato.
// Elegirla es obligatorio (Erwin, 2026-10-13): sin elección no hay contrato.
const NEW_SUB_QUOTATION = '__new_sub__';
const isFreeQuotation = (q) => ['draft', 'quoted'].includes(q.status) && (q.contracts || []).length === 0;
const defaultContractQuotation = (family, recordQuotationId, startDate) => {
  const members = [family.root, ...(family.subQuotations || [])];
  const own = members.find((m) => m.uid === recordQuotationId);
  if (own && isFreeQuotation(own)) return own.uid;
  const sameDate = members.find((m) => isFreeQuotation(m) && m.period?.startDate === startDate);
  if (sameDate) return sameDate.uid;
  // Madre archivada: no hay subcotización nueva posible → sin opción por defecto
  return family.root.status === 'archived' ? '' : NEW_SUB_QUOTATION;
};

/**
 * Detalle de la ficha: datos + documentos + acciones del flujo
 * (entregar → validar con datos operativos → pasar a contrato con causal).
 * Post-export: la edición exige confirmación (decisión 6).
 */
const ErpEmployeeDetailLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { employeeRecordId } = useParams();
  const { organization, authToken, showAlert } = useStore.getState();

  const [record, setRecord] = useState(null);
  const [quotationLabel, setQuotationLabel] = useState('');
  const [form, setForm] = useState(emptyEmployeeForm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);

  // dialogs
  const [confirmExportOpen, setConfirmExportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [validateOpen, setValidateOpen] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [causes, setCauses] = useState([]);
  const [causeCode, setCauseCode] = useState('');
  const [contractDates, setContractDates] = useState({ startDate: '', endDate: '' });
  const [balance, setBalance] = useState(null);
  // familia de la cotización de la ficha (madre + subs) y la elegida
  const [quotationFamily, setQuotationFamily] = useState(null);
  const [quotationFamilyError, setQuotationFamilyError] = useState(false);
  const [contractQuotation, setContractQuotation] = useState('');

  // upload de documentos
  const fileInputRef = useRef(null);
  const [docType, setDocType] = useState('id_card');
  const [uploading, setUploading] = useState(false);

  // Documentos en Talana (PLAN_DOCUMENTOS_TALANA §4.3): estado por documento,
  // «Enviar a Talana» y «Enviar también a Talana» al subir
  const [talanaDocs, setTalanaDocs] = useState(null); // { configured, environment, categories, person }
  const [talanaDialogOpen, setTalanaDialogOpen] = useState(false);
  const [sendToTalana, setSendToTalana] = useState(true);
  const [talanaCategory, setTalanaCategory] = useState(TALANA_CATEGORY_BY_TYPE.id_card);
  const [talanaCategoryTouched, setTalanaCategoryTouched] = useState(false);
  const [talanaVisible, setTalanaVisible] = useState(true);
  const [talanaVisibleTouched, setTalanaVisibleTouched] = useState(false);

  const headers = { headers: { Authorization: `Bearer ${authToken}` } };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/erp/employee-records/${employeeRecordId}`, {
        params: { organizationId: organization },
        ...headers,
      });
      if (response.data.response?.status_response === 'OK') {
        const data = response.data.response.data;
        setRecord(data);
        setForm(toEmployeeForm(data));
        if (data.quotationId) {
          try {
            const q = await api.get(`/erp/quotations/${data.quotationId}`, {
              params: { organizationId: organization },
              ...headers,
            });
            const quotation = q.data.response?.data;
            if (quotation) setQuotationLabel(`${quotation.quotationNumber ? quotation.quotationNumber + ' · ' : ''}${quotation.clientName} — ${quotation.jobTitleName}`);
          } catch (e) { console.error(e); }
        }
      } else {
        setError(t('erp.employees.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.employees.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && employeeRecordId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, employeeRecordId]);

  // Estado en Talana de los documentos de la persona (solo lee Amanda; no llama a Talana)
  const loadTalanaDocs = async () => {
    try {
      const response = await api.get(`/erp/employee-records/${employeeRecordId}/talana-documents`, {
        params: { organizationId: organization },
        ...headers,
      });
      // Mismo formato que export-talana: el objeto directo en response.data.response
      const body = response.data.response;
      setTalanaDocs(body && body.person ? body : null);
    } catch (err) {
      console.error(err);
      setTalanaDocs(null);
    }
  };

  useEffect(() => {
    if (organization && employeeRecordId) loadTalanaDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, employeeRecordId]);

  // Mientras haya documentos en cola o enviándose, consultar el avance
  const talanaInProgress = (talanaDocs?.person?.documents || [])
    .some((d) => ['queued', 'sending'].includes(d.talana?.status));
  useEffect(() => {
    if (!talanaInProgress || talanaDialogOpen) return undefined;
    const timer = setInterval(loadTalanaDocs, TALANA_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talanaInProgress, talanaDialogOpen]);

  const talanaCategories = talanaDocs?.categories || [];
  const canSendToTalana = Boolean(talanaDocs?.person?.canSend) && talanaDocs?.configured !== false;
  const talanaDocByKey = (key) => (talanaDocs?.person?.documents || []).find((d) => d.key === key);

  const handleDocTypeChange = (value) => {
    setDocType(value);
    if (!talanaCategoryTouched) {
      const categoria = TALANA_CATEGORY_BY_TYPE[value] || TALANA_CATEGORY_BY_TYPE.other;
      setTalanaCategory(categoria);
      const visible = knownVisibility(categoria, talanaCategories);
      if (!talanaVisibleTouched && visible !== null) setTalanaVisible(visible);
    }
  };

  const handleTalanaCategoryChange = (value) => {
    setTalanaCategory(value);
    setTalanaCategoryTouched(true);
    const visible = knownVisibility(value, talanaCategories);
    if (!talanaVisibleTouched && visible !== null) setTalanaVisible(visible);
  };

  /** Envía a Talana un documento recién subido a la ficha (segundo plano) */
  const sendUploadedToTalana = async (documentUid) => {
    try {
      const response = await api.post(
        `/erp/employee-records/${employeeRecordId}/talana-documents/send`,
        {
          organizationId: organization,
          documents: [{
            key: `record:${documentUid}`,
            categoria: canonicalCategory(talanaCategory, talanaCategories),
            puedeVerloElTrabajador: Boolean(talanaVisible),
          }],
        },
        headers
      );
      const body = response.data.response;
      if (body && body.person) setTalanaDocs(body);
      if (body?.accepted > 0) showAlert(t('erp.employees.docs.talana.sending'), 'info');
      else showAlert(t('erp.employees.docs.talana.notSent'), 'warning');
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      showAlert(
        code === 'ALREADY_RUNNING'
          ? t('erp.export.talana.docs.alreadyRunning')
          : t('erp.employees.docs.talana.notSent'),
        'warning'
      );
      loadTalanaDocs();
    }
  };

  const handleFieldChange = (path, value) =>
    setForm((prev) => applyEmployeeFieldChange(prev, path, value));

  const doSave = async (confirmAfterExport = false) => {
    if (Object.keys(employeeFormatErrors(form)).length > 0) {
      showAlert(t('erp.validation.fixErrors'), 'warning');
      return;
    }
    try {
      setWorking(true);
      const response = await api.put(
        `/erp/employee-records/${employeeRecordId}`,
        { organizationId: organization, ...toEmployeePayload(form), confirmAfterExport },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setRecord(response.data.response.data);
        showAlert(t('erp.employees.saveSuccess'), 'success');
        setConfirmExportOpen(false);
      }
    } catch (err) {
      if (err.response?.data?.response?.code === 'CONFIRM_AFTER_EXPORT_REQUIRED') {
        setConfirmExportOpen(true);
      } else {
        console.error(err);
        showAlert(t('erp.employees.saveError'), 'error');
      }
    } finally {
      setWorking(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setWorking(true);
      const response = await api.post(
        `/erp/employee-records/${employeeRecordId}/submit`,
        { organizationId: organization },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setRecord(response.data.response.data);
        showAlert(t('erp.employees.submitSuccess'), 'success');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.employees.saveError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  // Fase B: los datos viven en la ficha; validar guarda los cambios del
  // formulario y luego pide la transición (el backend re-verifica completitud)
  const handleValidate = async () => {
    try {
      setWorking(true);
      await api.put(
        `/erp/employee-records/${employeeRecordId}`,
        { organizationId: organization, ...toEmployeePayload(form) },
        headers
      );
      const response = await api.post(
        `/erp/employee-records/${employeeRecordId}/validate`,
        { organizationId: organization },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        setRecord(response.data.response.data);
        setValidateOpen(false);
        showAlert(t('erp.employees.validateSuccess'), 'success');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      showAlert(
        code === 'RECORD_INCOMPLETE'
          ? t('erp.employees.recordIncomplete')
          : t('erp.employees.saveError'),
        'error'
      );
    } finally {
      setWorking(false);
    }
  };

  // Familia de la cotización de la ficha (madre + subs): de ahí se elige,
  // obligatoriamente, la cotización de la persona. Si falla, Reintentar.
  const loadQuotationFamily = (startDate) => {
    setQuotationFamily(null);
    setQuotationFamilyError(false);
    setContractQuotation('');
    if (!record.quotationId) {
      setQuotationFamilyError(true);
      return;
    }
    api.get(`/erp/quotations/${record.quotationId}/family`, {
      params: { organizationId: organization },
      ...headers,
    })
      .then((response) => {
        const family = response.data.response?.data || null;
        if (!family) {
          setQuotationFamilyError(true);
          return;
        }
        setQuotationFamily(family);
        setContractQuotation(defaultContractQuotation(family, record.quotationId, startDate));
      })
      .catch((err) => {
        console.error(err);
        setQuotationFamilyError(true);
      });
  };

  const openContractDialog = async () => {
    const startDate = record.employment?.startDate || '';
    setCauseCode('');
    setBalance(null);
    setContractDates({
      startDate,
      endDate: record.employment?.endDate || '',
    });
    setContractOpen(true);
    loadQuotationFamily(startDate);
    try {
      const response = await api.get('/erp/hiring-causes', {
        params: { organizationId: organization, status: 'active' },
        ...headers,
      });
      setCauses(response.data.response?.data || []);
    } catch (err) {
      console.error(err);
      showAlert(t('erp.hiringCauses.loadError'), 'error');
    }
  };

  // saldo de causal (advertencia previa, spec: agotar causal debe advertirse)
  // Tipo de contrato de la ficha (referencia Talana con kind, o enum legado).
  // Indefinido: sin fecha de término ni causal EST (2026-09-24).
  const contractTypeValue = form.employment?.contractType;
  const contractKind = isTalanaRef(contractTypeValue)
    ? (contractTypeValue.kind || contractKindFromName(contractTypeValue.name))
    : (typeof contractTypeValue === 'string' ? contractTypeValue : null);
  const isIndefinite = contractKind === 'indefinite';

  useEffect(() => {
    const fetchBalance = async () => {
      if (!contractOpen || !causeCode || !record?.personal?.rut || isIndefinite) { setBalance(null); return; }
      try {
        const days =
          contractDates.startDate && contractDates.endDate
            ? Math.round(
                (new Date(`${contractDates.endDate}T00:00:00`) - new Date(`${contractDates.startDate}T00:00:00`)) / 86400000
              ) + 1
            : 0;
        const response = await api.get('/erp/contracts/cause-balance', {
          params: {
            organizationId: organization,
            workerRut: record.personal.rut,
            hiringCauseCode: causeCode,
            days,
          },
          ...headers,
        });
        setBalance(response.data.response?.data || null);
      } catch (err) {
        console.error(err);
        setBalance(null);
      }
    };
    fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractOpen, causeCode, contractDates.startDate, contractDates.endDate]);

  const handleCreateContract = async () => {
    if (!contractDates.startDate || (!isIndefinite && (!causeCode || !contractDates.endDate))) {
      showAlert(t('erp.common.requiredFields'), 'warning');
      return;
    }
    if (!quotationFamily || !contractQuotation) {
      showAlert(t('erp.contracts.form.quotationRequired'), 'warning');
      return;
    }
    try {
      setWorking(true);
      const response = await api.post(
        '/erp/contracts',
        {
          organizationId: organization,
          employeeRecordId,
          hiringCauseCode: isIndefinite ? null : causeCode,
          dates: { startDate: contractDates.startDate, endDate: isIndefinite ? null : contractDates.endDate },
          // cotización de esta persona (obligatoria): una de la familia o una sub nueva
          ...(contractQuotation === NEW_SUB_QUOTATION
            ? { createSubQuotation: true }
            : { quotationId: contractQuotation }),
        },
        headers
      );
      if (response.data.response?.status_response === 'OK') {
        const usedQuotation = response.data.response.quotation;
        showAlert(
          usedQuotation?.created
            ? t('erp.contracts.createdWithSub', { number: usedQuotation.quotationNumber || '' })
            : t('erp.contracts.createSuccess'),
          'success'
        );
        const uid = response.data.response.data?.uid;
        navigate(uid ? `/erp/contracts/${uid}` : '/erp/contracts');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      const message = err.response?.data?.errorMessage || '';
      if (code === 'QUOTATION_HAS_CONTRACT') showAlert(t('erp.contracts.form.quotationTaken'), 'error');
      else if (code === 'QUOTATION_CHOICE_REQUIRED') showAlert(t('erp.contracts.form.quotationRequired'), 'warning');
      else if (code === 'QUOTATION_ARCHIVED') showAlert(t('erp.contracts.form.quotationArchivedError'), 'error');
      else if (code === MINIMUM_WAGE_CODE) {
        // Fase 1b: con la fecha de ingreso, el sueldo base queda bajo el ingreso mínimo de ese mes
        showAlert(t('erp.contracts.form.belowMinimum', { reason: minimumWageMessage(t, err.response.data.response) }), 'error');
      }
      else if (['PARAMETER_SET_MISSING', 'PARAMETER_SET_INCOMPLETE', 'ENGINE_NOT_READY'].includes(code)) {
        showAlert(t('erp.quotations.parameterSetMissing'), 'warning');
      } else showAlert(message || t('erp.contracts.saveError'), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('document', file);
      fd.append('organizationId', organization);
      fd.append('type', docType);
      const response = await api.post(
        `/erp/employee-records/${employeeRecordId}/documents?organizationId=${organization}`,
        fd,
        { headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.response?.status_response === 'OK') {
        const before = new Set((record.documents || []).map((d) => d.uid));
        const updated = response.data.response.data;
        setRecord(updated);
        showAlert(t('erp.employees.docs.uploadSuccess'), 'success');
        // Persona con contrato en Talana: el documento nuevo se envía también allá
        const added = (updated?.documents || []).find((d) => !before.has(d.uid));
        if (canSendToTalana && sendToTalana && added && String(talanaCategory || '').trim()) {
          await sendUploadedToTalana(added.uid);
        } else {
          loadTalanaDocs();
        }
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.employees.docs.uploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveDoc = async (documentId) => {
    try {
      const response = await api.delete(
        `/erp/employee-records/${employeeRecordId}/documents/${documentId}`,
        { params: { organizationId: organization }, ...headers }
      );
      if (response.data.response?.status_response === 'OK') {
        setRecord(response.data.response.data);
        loadTalanaDocs();
      }
    } catch (err) {
      console.error(err);
      showAlert(t('erp.employees.docs.uploadError'), 'error');
    }
  };

  const handleDelete = async () => {
    try {
      setWorking(true);
      const response = await api.delete(`/erp/employee-records/${employeeRecordId}`, {
        params: { organizationId: organization },
        ...headers,
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.employees.deleteSuccess'), 'success');
        navigate('/erp/employees');
      }
    } catch (err) {
      console.error(err);
      const code = err.response?.data?.response?.code;
      showAlert(
        code === 'RECORD_HAS_CONTRACT'
          ? t('erp.employees.deleteHasContract')
          : t('erp.employees.deleteError'),
        'error'
      );
      setDeleteOpen(false);
    } finally {
      setWorking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
    );
  }

  if (error || !record) {
    return (
      <Box sx={{ p: 3 }}><Alert severity="error">{error || t('erp.employees.loadError')}</Alert></Box>
    );
  }

  const fullName = [record.personal?.names, record.personal?.firstLastName, record.personal?.secondLastName]
    .filter(Boolean).join(' ');
  // Sin la cotización de la persona elegida no se crea el contrato: el botón
  // queda deshabilitado con el motivo (cargando, error o falta elegir)
  const quotationFamilyLoading = Boolean(record.quotationId) && !quotationFamily && !quotationFamilyError;
  let createContractBlock = '';
  if (quotationFamilyLoading) createContractBlock = t('erp.contracts.form.quotationLoading');
  else if (quotationFamilyError) createContractBlock = t('erp.contracts.form.quotationLoadError');
  else if (!contractQuotation) createContractBlock = t('erp.contracts.form.quotationRequired');

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/erp/employees')} sx={{ textTransform: 'none', mb: 1 }}>
        {t('erp.employees.title')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{fullName || t('erp.employees.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {formatRut(record.personal?.rut)}{quotationLabel ? ` · ${quotationLabel}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={t(`erp.employees.status.${record.status}`)}
            color={EMPLOYEE_STATUS_COLORS[record.status] || 'default'} />
          {record.status === 'draft' && (
            <Tooltip title={t('erp.tooltips.employee.submit')}>
              <Button variant="outlined" size="small" startIcon={<SubmitIcon />} disabled={working}
                onClick={handleSubmit} sx={{ textTransform: 'none' }}>
                {t('erp.employees.actions.submit')}
              </Button>
            </Tooltip>
          )}
          {record.status === 'submitted' && (
            <Tooltip title={t('erp.tooltips.employee.validate')}>
              <Button variant="outlined" size="small" startIcon={<ValidateIcon />} disabled={working}
                onClick={() => setValidateOpen(true)} sx={{ textTransform: 'none' }}>
                {t('erp.employees.actions.validate')}
              </Button>
            </Tooltip>
          )}
          {record.status === 'validated' && (
            <Tooltip title={t('erp.tooltips.employee.toContract')}>
              <Button variant="contained" size="small" startIcon={<ContractIcon />} disabled={working}
                onClick={openContractDialog}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
                {t('erp.employees.actions.toContract')}
              </Button>
            </Tooltip>
          )}
          {record.status !== 'contracted' && (
            <Tooltip title={t('erp.employees.deleteConfirmText')}>
              <Button variant="outlined" color="error" size="small" startIcon={<DeleteIcon />} disabled={working}
                onClick={() => setDeleteOpen(true)} sx={{ textTransform: 'none' }}>
                {t('erp.employees.deleteRecord')}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 2 }}>
        <ErpEmployeeFormFields form={form} onChange={handleFieldChange} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="contained" disabled={working || Object.keys(employeeFormatErrors(form)).length > 0} onClick={() => doSave(false)}
            startIcon={working ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {working ? t('erp.common.saving') : t('erp.common.save')}
          </Button>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t('erp.employees.docs.title')}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField select size="small" label={t('erp.employees.docs.type')} value={docType}
              onChange={(e) => handleDocTypeChange(e.target.value)} sx={{ minWidth: 220 }}>
              {DOC_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{t(`erp.employees.docs.types.${type}`)}</MenuItem>
              ))}
            </TextField>
            <Button size="small" variant="outlined" startIcon={<UploadIcon />} disabled={uploading}
              onClick={() => fileInputRef.current?.click()} sx={{ textTransform: 'none' }}>
              {uploading ? t('erp.common.saving') : t('erp.employees.docs.upload')}
            </Button>
            <input ref={fileInputRef} type="file" hidden onChange={handleUpload} data-testid="employee-doc-file-input" />
            {canSendToTalana && (
              <Tooltip title={t('erp.employees.docs.talana.sendHint')}>
                <span>
                  <Button size="small" variant="outlined" startIcon={<TalanaIcon />} disabled={uploading}
                    onClick={() => setTalanaDialogOpen(true)} sx={{ textTransform: 'none' }}>
                    {t('erp.employees.docs.talana.send')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Box>
        {/* Persona con contrato en Talana: lo que se suba se puede mandar también allá */}
        {canSendToTalana && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end', mb: 1 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={sendToTalana} onChange={(e) => setSendToTalana(e.target.checked)} />}
              label={<Typography variant="body2">{t('erp.employees.docs.talana.alsoSend')}</Typography>}
            />
            {sendToTalana && (
              <>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={talanaCategories.map((c) => c.value)}
                  value={talanaCategory}
                  inputValue={talanaCategory}
                  onChange={(e, value) => handleTalanaCategoryChange(value || '')}
                  onInputChange={(e, value, reason) => {
                    if (reason === 'input' || reason === 'clear') handleTalanaCategoryChange(value);
                  }}
                  onBlur={() => setTalanaCategory(canonicalCategory(talanaCategory, talanaCategories))}
                  sx={{ width: 240 }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('erp.employees.docs.talana.category')}
                      error={!String(talanaCategory || '').trim()}
                      inputProps={{ ...params.inputProps, maxLength: 100 }} />
                  )}
                />
                <Tooltip title={t('erp.export.talana.docs.visibleHint')}>
                  <FormControlLabel
                    control={(
                      <Switch size="small" checked={talanaVisible}
                        onChange={(e) => { setTalanaVisible(e.target.checked); setTalanaVisibleTouched(true); }} />
                    )}
                    label={<Typography variant="body2">{t('erp.employees.docs.talana.visible')}</Typography>}
                  />
                </Tooltip>
              </>
            )}
          </Box>
        )}
        <Divider sx={{ mb: 1 }} />
        {(record.documents || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('erp.employees.docs.empty')}</Typography>
        ) : (
          (record.documents || []).map((doc) => {
            const inTalana = talanaDocByKey(`record:${doc.uid}`);
            return (
              <Box key={doc.uid} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                <Chip size="small" variant="outlined" label={t(`erp.employees.docs.types.${doc.type}`) || doc.type} />
                <Link href={doc.url} target="_blank" rel="noopener" underline="hover" sx={{ flexGrow: 1 }}>
                  {doc.name}
                </Link>
                {inTalana && <TalanaDocStatusChip talana={inTalana.talana} />}
                {inTalana && canSendToTalana && inTalana.talana?.status === 'not_sent' && (
                  <Chip size="small" variant="outlined" label={t('erp.employees.docs.talana.notSentChip')} />
                )}
                {record.status !== 'contracted' && (
                  <Tooltip title={inTalana?.talana?.status === 'sent' ? t('erp.employees.docs.talana.deleteKeepsTalana') : t('delete')}>
                    <IconButton size="small" onClick={() => handleRemoveDoc(doc.uid)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            );
          })
        )}
      </Paper>

      <ErpTalanaDocumentsDialog
        open={talanaDialogOpen}
        onClose={() => { setTalanaDialogOpen(false); loadTalanaDocs(); }}
        mode="documents"
        employeeRecordId={employeeRecordId}
        onSent={loadTalanaDocs}
        onDocumentsChanged={(updated) => { if (updated) setRecord(updated); }}
      />

      {/* Confirmación de eliminación de ficha */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.employees.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('erp.employees.deleteConfirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" disabled={working} onClick={handleDelete}
            sx={{ textTransform: 'none' }}>
            {t('erp.employees.deleteRecord')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmación post-export (decisión 6) */}
      <Dialog open={confirmExportOpen} onClose={() => setConfirmExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.employees.postExport.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t('erp.employees.postExport.message')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExportOpen(false)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="warning" disabled={working} onClick={() => doSave(true)}
            sx={{ textTransform: 'none' }}>
            {t('erp.employees.postExport.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validación (fase B): checklist de completitud — los datos se
          completan en la ficha, aquí solo se revisa y confirma */}
      <Dialog open={validateOpen} onClose={() => setValidateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('erp.employees.validateDialog.title')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('erp.employees.validateDialog.checklistSubtitle')}
          </Typography>
          {(() => {
            const missing = missingForValidation(form);
            const missingPaths = new Set(missing.map(([path]) => path));
            return (
              <>
                {REQUIRED_ROWS.map(([path, labelKey]) => (
                  <Box key={path} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                    {missingPaths.has(path)
                      ? <MissingIcon fontSize="small" color="error" />
                      : <CheckIcon fontSize="small" color="success" />}
                    <Typography variant="body2">{t(labelKey)}</Typography>
                  </Box>
                ))}
                {missing.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('erp.employees.validateDialog.missingHint')}
                  </Alert>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  {t('erp.employees.validateDialog.saveNote')}
                </Typography>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidateOpen(false)} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
          <Button variant="contained" disabled={working || missingForValidation(form).length > 0}
            onClick={handleValidate}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
            {t('erp.employees.actions.validate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pasar a contrato: causal + fechas + saldo (spec 3.6.39) */}
      <Dialog open={contractOpen} onClose={() => setContractOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('erp.contracts.createTitle')}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info" icon={false}>
                {t('erp.contracts.form.contractTypeFromRecord')}: <strong>{refName(contractTypeValue) || t('erp.contracts.form.contractTypeUnknown')}</strong>
                {isIndefinite && ` — ${t('erp.contracts.form.indefiniteHint')}`}
              </Alert>
            </Grid>
            {quotationFamily && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" required label={t('erp.contracts.form.quotation')}
                  value={contractQuotation} onChange={(e) => setContractQuotation(e.target.value)}
                  helperText={contractQuotation === NEW_SUB_QUOTATION
                    ? t('erp.contracts.form.quotationNewHint')
                    : t('erp.contracts.form.quotationHint')}>
                  {[quotationFamily.root, ...(quotationFamily.subQuotations || [])].map((q) => {
                    const people = (q.contracts || []).map((c) => c.workerName).filter(Boolean).join(', ');
                    let label = t('erp.contracts.form.quotationOption', {
                      number: q.quotationNumber, date: formatDate(q.period?.startDate),
                    });
                    if (q.status === 'archived') label = t('erp.contracts.form.quotationArchived', { number: q.quotationNumber });
                    else if (!isFreeQuotation(q)) {
                      label = people
                        ? t('erp.contracts.form.quotationUsedBy', { number: q.quotationNumber, person: people })
                        : t('erp.contracts.form.quotationUsed', { number: q.quotationNumber });
                    }
                    return (
                      <MenuItem key={q.uid} value={q.uid} disabled={!isFreeQuotation(q)}>{label}</MenuItem>
                    );
                  })}
                  <MenuItem value={NEW_SUB_QUOTATION} disabled={quotationFamily.root.status === 'archived'}>
                    {t('erp.contracts.form.quotationNew', { number: quotationFamily.root.quotationNumber || '' })}
                  </MenuItem>
                </TextField>
              </Grid>
            )}
            {quotationFamilyError && (
              <Grid item xs={12}>
                <Alert severity="error"
                  action={(
                    <Button color="inherit" size="small" sx={{ textTransform: 'none' }}
                      onClick={() => loadQuotationFamily(contractDates.startDate)}>
                      {t('erp.contracts.form.quotationRetry')}
                    </Button>
                  )}>
                  {t('erp.contracts.form.quotationLoadError')}
                </Alert>
              </Grid>
            )}
            {!isIndefinite && (
              <Grid item xs={12}>
                <TextField select fullWidth size="small" label={t('erp.contracts.form.hiringCause')}
                  helperText={t('erp.tooltips.employee.hiringCause')}
                  value={causeCode} onChange={(e) => setCauseCode(e.target.value)}>
                  {causes.map((cause) => (
                    <MenuItem key={cause.uid} value={cause.code}>
                      {cause.code} — {cause.name}{cause.maxDays ? ` (máx. ${cause.maxDays}d)` : ''}
                    </MenuItem>
                  ))}
                </TextField>
                {causes.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('erp.contracts.noCauses')}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid item xs={6}>
              <ErpDateField label={t('erp.quotations.form.startDate')} value={contractDates.startDate}
                onChange={(value) => setContractDates((p) => ({ ...p, startDate: value }))} />
            </Grid>
            {!isIndefinite && (
              <Grid item xs={6}>
                <ErpDateField label={t('erp.quotations.form.endDate')} value={contractDates.endDate}
                  onChange={(value) => setContractDates((p) => ({ ...p, endDate: value }))} />
              </Grid>
            )}
            {!isIndefinite && balance && (
              <Grid item xs={12}>
                <Alert severity={balance.warning ? 'error' : 'info'}>
                  {t('erp.contracts.balance.summary', {
                    consumed: balance.daysConsumed,
                    limit: balance.legalLimitDays ?? '∞',
                    available: balance.daysAvailable ?? '∞',
                  })}
                  {balance.warning && ` — ${t('erp.contracts.balance.warning')}`}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractOpen(false)} sx={{ textTransform: 'none' }}>{t('erp.common.cancel')}</Button>
          <Tooltip title={createContractBlock}>
            <span>
              <Button variant="contained" disabled={working || Boolean(createContractBlock)} onClick={handleCreateContract}
                startIcon={quotationFamilyLoading ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, textTransform: 'none' }}>
                {t('erp.contracts.create')}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpEmployeeDetailLayout;
